import {
  Menu,
  User,
  ChevronDown,
  LogOut,
  Bell,
  X,
  Trash2,
} from "lucide-react";
import {
  retrieveData,
  removeData,
} from "../../LocalConnection/LocalConnection.js";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

import {
  get_notifications,
  un_red_notifications,
  mark_notification_read,
  server_post_json,
} from "../../ServiceConnection/serviceconnection.js";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
// } from "@mui/material";

export default function Header({ collapsed, setCollapsed }) {
  const lazyx_admin_email = retrieveData("lazyx_admin_email");
  const navigate = useNavigate();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const audioRef = useRef(null);
  const pollingRef = useRef(null);
  const soundLoopRef = useRef(null);
  useEffect(() => {
    const onTokenExpired = () => {
      clearInterval(pollingRef.current); // 🔥 STOP POLLING
      setSessionExpired(true);
    };
  
    window.addEventListener("TOKEN_EXPIRED", onTokenExpired);
    return () =>
      window.removeEventListener("TOKEN_EXPIRED", onTokenExpired);
  }, []);
  
  /* ================= AUDIO UNLOCK ================= */
  useEffect(() => {
    const unlock = async () => {
      if (soundEnabled || !audioRef.current) return;
      try {
        audioRef.current.muted = true;
        await audioRef.current.play();
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current.muted = false;
        setSoundEnabled(true);
      } catch {}
    };

    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);

    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [soundEnabled]);

  /* ================= API ================= */
  const fetchNotifications = async () => {
    try {
      const res = await server_post_json(get_notifications);
      setNotifications(res?.data?.notifications || []);
    } catch {

    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await server_post_json(un_red_notifications);
      setUnreadCount(res?.data?.unreadCount || 0);
    } catch {}
  };

  /* ================= SOUND LOOP ================= */
  useEffect(() => {
    if (
      soundEnabled &&
      unreadCount > 0 &&
      !notifyOpen &&
      !soundLoopRef.current
    ) {
      soundLoopRef.current = setInterval(() => {
        audioRef.current?.play().catch(() => {});
      }, 10000);
    }

    if (unreadCount === 0 || notifyOpen) {
      clearInterval(soundLoopRef.current);
      soundLoopRef.current = null;
    }

    return () => {
      clearInterval(soundLoopRef.current);
      soundLoopRef.current = null;
    };
  }, [unreadCount, notifyOpen, soundEnabled]);

  /* ================= ACTIONS ================= */
  const markAsRead = async (id) => {
    await server_post_json(mark_notification_read, { notification_id: id });
    fetchNotifications();
    fetchUnreadCount();
  };

  const clearAll = async () => {
    if (notifications.length === 0) return;
  
    setClearing(true);
  
    // iOS-style stagger animation
    notifications.forEach((_, i) => {
      setTimeout(() => {
        setNotifications((prev) => prev.slice(1));
      }, i * 70); // 👈 smooth cascade
    });
  
    // backend call (parallel, silent)
    try {
      for (const n of notifications) {
        if (!n.isRead) {
          await server_post_json(mark_notification_read, {
            notification_id: n._id,
          });
        }
      }
    } catch {}
  
    // close panel after animation
    setTimeout(() => {
      setNotifyOpen(false);
      setClearing(false);
      setUnreadCount(0);
    }, notifications.length * 70 + 200);
  };
  

  /* ================= POLLING ================= */
  useEffect(() => {
    const token = retrieveData("lazyx_admin_token");
    if (!token) return; // 🚫 STOP HERE
  
    fetchNotifications();
    fetchUnreadCount();
  
    pollingRef.current = setInterval(() => {
      fetchUnreadCount();
    }, 5000);
  
    return () => clearInterval(pollingRef.current);
  }, []);
  

  /* ================= LOGOUT ================= */
  const handleSignOut = () => {
    removeData();
    navigate("/", { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 bg-white border-b relative">
    

      <div className="flex items-center  justify-between px-4 py-[11.50px]">
        {/* LEFT */}
        <button
          className="hidden md:inline-flex h-9 px-3 rounded-md hover:bg-gray-100"
          onClick={() => setCollapsed(!collapsed)}
        >
          <Menu />
        </button>

        {/* RIGHT */}
        <div className="flex items-center justify-between w-full md:w-auto  gap-4">
          {/* 🔔 BELL */}
          <div className="relative">
            <button
              onClick={() => setNotifyOpen(!notifyOpen)}
              className={`relative h-10q w-10 rounded-full flex items-center justify-center transition
              ${unreadCount > 0 ? "ring-2 ring-green-400 ring-offset-2 animate-pulse" : ""}
              hover:bg-gray-100`}
            >
              <Bell size={18} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs px-1.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>

            {notifyOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setNotifyOpen(false)}
                />
                <div className="absolute right-0 top-12 w-96 bg-white border rounded-xl shadow-2xl z-50 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b">
                    <h3 className="font-semibold">Notifications</h3>
                    <div className="flex gap-2">
                    <button
  onClick={clearAll}
  disabled={clearing}
  className={`text-xs transition-all ${
    clearing
      ? "text-gray-400 cursor-not-allowed"
      : "text-red-500 hover:underline"
  }`}
>
  {clearing ? "Clearing…" : "Clear all"}
</button>

                      <X size={18} onClick={() => setNotifyOpen(false)} />
                    </div>
                  </div>

                  <div className="max-h-[70vh] overflow-y-auto divide-y">
                    {notifications.length === 0 && (
                      <div className="p-10 text-center text-gray-400">
                        No new notifications ✨
                      </div>
                    )}

                    {notifications.map((n) => (
                     <div
                     key={n._id}
                     onClick={() => markAsRead(n._id)}
                     className={`group p-4 flex gap-3 cursor-pointer transition-all duration-300
                       ${n.isRead ? "opacity-60" : "bg-blue-50"}
                       ${clearing ? "opacity-0 -translate-x-4" : "hover:bg-gray-50"}
                     `}
                   >
                   
                        <div className="flex-1">
                          <p className="font-medium text-sm">{n.title}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {n.message}
                          </p>
                        </div>

                        <Trash2
                          size={16}
                          className="text-gray-400 opacity-0 group-hover:opacity-100 hover:text-red-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* PROFILE */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-3 h-10 rounded-md hover:bg-gray-100"
            >
              <div className="h-8 w-8 rounded-full bg-black text-white flex items-center justify-center">
                <User size={16} />
              </div>
              <ChevronDown size={16} />
            </button>

            {dropdownOpen && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setDropdownOpen(false)}
                />
                <div className="absolute right-0 top-12 w-56 bg-white border rounded-lg shadow-xl z-50">
                  <div className="p-4 border-b">
                    <p className="font-medium">Admin</p>
                    <p className="text-xs text-gray-500">
                      {lazyx_admin_email}
                    </p>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="w-full px-4 py-3 flex gap-2 text-red-600 hover:bg-red-50"
                  >
                    <LogOut size={16} /> Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 🔊 AUDIO */}
      {/* <audio
        ref={audioRef}
        src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3"
        preload="auto"
      /> */}

{/* <Dialog
  open={sessionExpired}
  disableEscapeKeyDown
  maxWidth="xs"
  fullWidth
>
  <DialogTitle>Session Expired</DialogTitle>

  <DialogContent dividers>
    <p className="text-sm text-gray-600">
      Your session has expired. Please login again to continue.
    </p>
  </DialogContent>

  <DialogActions>
    <Button
      variant="contained"
      color="error"
      fullWidth
      onClick={() => {
        removeData();                 // token + email clear
        setSessionExpired(false);
        navigate("/", { replace: true });
      }}
    >
      Go to Login
    </Button>
  </DialogActions>
</Dialog> */}


    </header>
  );
}
